import { JSX, KeyboardEvent } from 'react';
import { MenuItem, TextField } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { ImportOption } from './csv-import-types';
import { splitIds } from './csv-import';

interface ReferenceCellProps {
    /** The cell as stored: one id, or ids separated by semicolons when `multiple`. */
    value: string;
    options: ImportOption[];
    multiple: boolean;
    label: string;
    invalid: boolean;
    onChange: (value: string) => void;
}

/**
 * An id column as a dropdown that is always showing, loaded with the records it can point at.
 *
 * An id the list does not hold stays in the dropdown as "Unknown id", so the admin can see what
 * the file said and swap it for a real record, rather than the value vanishing on load.
 */
export const ReferenceCell = (props: ReferenceCellProps): JSX.Element => {
    const { value, options, multiple, label, invalid, onChange } = props;
    const ids: string[] = multiple ? splitIds(value) : (value === '' ? [] : [value]);
    const unknown: ImportOption[] = ids
        .filter((id: string): boolean => !options.some((option: ImportOption): boolean => option.value === id))
        // A names column can hold a name nothing matched, which is not an id at all.
        .map((id: string): ImportOption => ({ value: id, label: (/^[0-9]+$/.test(id) ? 'Unknown id ' : 'Not found: ') + id }));
    const all: ImportOption[] = [...unknown, ...options];
    const labelFor = (id: string): string =>
        all.find((option: ImportOption): boolean => option.value === id)?.label ?? id;

    return (
        <TextField
            select
            size="small"
            // No floating label in a grid cell, so no room is kept for one; the column header names it.
            hiddenLabel
            fullWidth
            value={ multiple ? ids : value }
            error={ invalid }
            // The grid takes arrow keys and Enter for its own navigation; inside an open select
            // they belong to the select.
            onKeyDown={ (event: KeyboardEvent): void => event.stopPropagation() }
            slotProps={ {
                htmlInput: { 'aria-label': label },
                select: {
                    multiple: multiple,
                    displayEmpty: true,
                    renderValue: (selected: unknown): string => {
                        const chosen: string[] = Array.isArray(selected) ? selected : (selected ? [String(selected)] : []);

                        return chosen.length === 0 ? 'None' : chosen.map(labelFor).join(', ');
                    },
                    onChange: (event: SelectChangeEvent<unknown>): void => {
                        const next: unknown = event.target.value;
                        onChange(Array.isArray(next) ? next.join(';') : String(next ?? ''));
                    },
                    MenuProps: { slotProps: { paper: { sx: { maxHeight: 360 } } } },
                },
            } }
            sx={ {
                '& .MuiSelect-select': { whiteSpace: 'normal', fontSize: '0.875rem' },
                '& .MuiSelect-select em': { color: 'text.secondary' },
            } }
        >
            { !multiple && <MenuItem value=""><em>None</em></MenuItem> }
            { unknown.map((option: ImportOption) => (
                <MenuItem key={ 'unknown-' + option.value } value={ option.value } sx={ { color: 'error.main' } }>
                    { option.label }
                </MenuItem>
            )) }
            { options.map((option: ImportOption) => (
                <MenuItem key={ option.value } value={ option.value }>{ option.label }</MenuItem>
            )) }
        </TextField>
    );
};
